import {AiAssetType, AssetSource, AssetType} from 'common/src/modules/Assets/Assets.enums';
import getAllAssetsFromTree from 'common/src/modules/Assets/utils/getAllAssetsFromTree';
import {loadBrandAssetTree} from 'common/src/modules/brandGuidelines/store/brandAssets/thunks/loadAssetTree';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for getting available assets
const inputSchema = z.object({
  source: z.nativeEnum(AssetSource).describe('Source of assets to retrieve'),
  assetType: z
    .nativeEnum(AiAssetType)
    .describe('Type of assets to retrieve. Only Image, Video, and Font are supported'),
});

function getAvailableAssetsTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.GetAvailableAssets,
    displayName: 'Getting available assets',
    description:
      'Retrieves assets from either template assets or brand assets, filtered by type. Returns assets with their id and content for agent consumption.',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
          error: 'No active composition found',
        };
      }

      try {
        // Validate that Font is not requested for BrandAssets
        if (input.source === AssetSource.BrandAssets && input.assetType === AiAssetType.Font) {
          return {
            content:
              'ERROR: Font assets are not available from BrandAssets. Only Image and Video are supported.',
          };
        }

        // Map AiAssetType to AssetType for filtering
        const targetAssetType = getMatchingEnumValue(AiAssetType, AssetType, input.assetType);

        if (targetAssetType === undefined) {
          return {
            content: `ERROR: Unsupported asset type: ${input.assetType}`,
          };
        }

        if (input.source === AssetSource.TemplateAssets) {
          // Template assets are loaded by the template page upon start
          const assetTree = store.getState().creativeTemplatePage.templateAssets.assetTree;

          if (!assetTree) {
            return {
              content:
                'ERROR: Template assets not loaded. Please ensure the template page has loaded.',
            };
          }

          // Extract all assets from the tree recursively
          const allAssets = getAllAssetsFromTree(assetTree);

          // Filter assets by the requested type
          const filteredAssets = allAssets.filter((asset) => asset.assetType === targetAssetType);

          // Return essential information for asset selection
          const assetList = filteredAssets.map((asset) => ({
            id: asset.id,
            name: asset.name || 'Unnamed Asset',
            extension: asset.extension || '',
            description: asset.contentMetadata?.aiContentMetadata?.metadataV1?.description || '',
            source: 'TemplateAssets',
          }));

          return {
            content: `Found ${assetList.length} active ${input.assetType.toLowerCase()} template assets: ${JSON.stringify(assetList, null, 2)}`,
          };
        } else {
          // BrandAssets path - load if not already loaded
          let assetTree = store.getState().brandGuidelinesPage.brandAssets.assetTree;

          if (!assetTree) {
            await store.dispatch(loadBrandAssetTree()).unwrap();
            assetTree = store.getState().brandGuidelinesPage.brandAssets.assetTree;
          }

          if (!assetTree) {
            return {
              content: 'ERROR: Failed to load brand assets.',
            };
          }

          // Extract all assets from the tree recursively
          const allAssets = getAllAssetsFromTree(assetTree);

          // Filter assets by the requested type
          const filteredAssets = allAssets.filter((asset) => asset.assetType === targetAssetType);

          // Return essential information for asset selection
          const assetList = filteredAssets.map((asset) => ({
            id: asset.id,
            name: asset.name || 'Unnamed Asset',
            extension: asset.extension || '',
            description: asset.contentMetadata?.aiContentMetadata?.metadataV1?.description || '',
            source: 'BrandAssets',
          }));

          return {
            content: `Found ${assetList.length} ${input.assetType.toLowerCase()} brand assets: ${JSON.stringify(assetList, null, 2)}`,
          };
        }
      } catch (error) {
        return {
          content: `ERROR: ${error instanceof Error ? error.message : 'Failed to get available assets'}`,
        };
      }
    },
  });
}

export default getAvailableAssetsTool;
